/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import isEmpty from 'lodash/isEmpty';
import * as constants from '../../constants/ServiceConstants';
import utils from '../../../../common/utils';

const hasLogContent = (logs) => logs != null && !isEmpty(logs);

export default class GetExecutionLogsService extends BaseService {
  constructor() {
    super(constants.GET_EXECUTION_LOGS, 'getExecutionLogsStatus');
  }

  async handleEvent(eventName, event) {
    const { payload, headers } = utils.buildRequestPartsFromPayload(event);
    const { requestId } = payload;
    const url = `/logs/${requestId}?${constants.EXECUTION_LOGS_AUDIT_FALLBACK_QUERY}`;

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').get(url, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').get(url, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const fromAudit = data?.source === constants.EXECUTION_LOGS_SOURCE_AUDIT;

    if (data && data.success && hasLogContent(data.logs)) {
      Framework.getStoreRegistry().getStore('graphTesterModel').setExecutionLogs(data, fromAudit);
      Framework.getEventManager().publish(constants.GET_EXECUTION_LOGS_SUCCESS, { data });
      return;
    }

    this.setExecutionLogsNotFound();
  }

  handleError(error) {
    const { response } = error;
    if (response?.status === 401) {
      utils.redirectToLogin();
    }

    this.setExecutionLogsFetchError();
  }

  setExecutionLogsFetchError() {
    const notification = {
      open: true,
      type: 'error',
      message: 'Error fetching execution logs'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
    Framework.getStoreRegistry()
      .getStore('graphTesterModel')
      .setExecutionLogsError(constants.EXECUTION_LOGS_ERROR_FETCH_FAILED);
    Framework.getEventManager().publish(constants.GET_EXECUTION_LOGS_ERROR);
  }

  setExecutionLogsNotFound() {
    const notification = {
      open: true,
      type: 'error',
      message: 'Error fetching execution logs'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
    Framework.getStoreRegistry()
      .getStore('graphTesterModel')
      .setExecutionLogsError(constants.EXECUTION_LOGS_ERROR_NOT_FOUND);
    Framework.getEventManager().publish(constants.GET_EXECUTION_LOGS_ERROR);
  }
}
